import { create } from "zustand";
import { supabase } from "../lib/supabase";

export interface FriendProfile {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface FollowState {
  following: Set<string>;
  friendProfiles: FriendProfile[];
  friendsLoading: boolean;
  loading: boolean;
  error: string | null;

  fetchFollowing: (userId: string) => Promise<void>;
  fetchFollowingProfiles: (userId: string) => Promise<void>;
  followUser: (targetUserId: string) => Promise<void>;
  unfollowUser: (targetUserId: string) => Promise<void>;
  isFollowing: (targetUserId: string) => boolean;
  clearError: () => void;
}

export const useFollowStore = create<FollowState>((set, get) => ({
  following: new Set<string>(),
  friendProfiles: [],
  friendsLoading: false,
  loading: false,
  error: null,

  fetchFollowing: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", userId);

      if (error) throw error;

      const followingSet = new Set(data.map((f) => f.following_id));
      set({ following: followingSet, loading: false });
    } catch (error) {
      const message = "Failed to fetch following list";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  fetchFollowingProfiles: async (userId: string) => {
    set({ friendsLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("follows")
        .select(
          "following_id, profiles!follows_following_id_fkey(id, username, full_name, avatar_url)"
        )
        .eq("follower_id", userId);

      if (error) throw error;

      const profiles: FriendProfile[] = (data || [])
        .filter((row) => row.profiles != null)
        .map((row) => {
          // Supabase returns a single object for foreign key joins, but generated types say array
          const p = row.profiles as unknown as FriendProfile;
          return {
            id: p.id,
            username: p.username,
            full_name: p.full_name,
            avatar_url: p.avatar_url,
          };
        });
      set({ friendProfiles: profiles, friendsLoading: false });
    } catch (error) {
      const message = "Failed to fetch friend profiles";
      console.error(message, error);
      set({ error: message, friendsLoading: false });
    }
  },

  followUser: async (targetUserId: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      set({ error: "Must be logged in to follow users" });
      return;
    }

    if (user.id === targetUserId) {
      set({ error: "Cannot follow yourself" });
      return;
    }

    set({ loading: true, error: null });
    try {
      const { error } = await supabase.from("follows").insert({
        follower_id: user.id,
        following_id: targetUserId,
      });

      if (error) throw error;

      // Update local state
      const newFollowing = new Set(get().following);
      newFollowing.add(targetUserId);
      set({ following: newFollowing, friendProfiles: [], loading: false });
    } catch (error) {
      const message = "Failed to follow user";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  unfollowUser: async (targetUserId: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      set({ error: "Must be logged in to unfollow users" });
      return;
    }

    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from("follows")
        .delete()
        .eq("follower_id", user.id)
        .eq("following_id", targetUserId);

      if (error) throw error;

      // Update local state
      const newFollowing = new Set(get().following);
      newFollowing.delete(targetUserId);
      set({ following: newFollowing, friendProfiles: [], loading: false });
    } catch (error) {
      const message = "Failed to unfollow user";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  isFollowing: (targetUserId: string) => {
    return get().following.has(targetUserId);
  },

  clearError: () => set({ error: null }),
}));
